import { cn } from "@/lib/utils";
import { ButtonHTMLAttributes, forwardRef } from "react";
import { getOrAssignVariant, trackVariantEvent } from "@/lib/abTest";
import { recordAbClick } from "@/lib/abTracking";

interface CTAButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary";
  size?: "default" | "lg";
  href?: string;
}

const CTAButton = forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, variant = "primary", size = "default", children, href, onClick, ...props }, ref) => {
    const baseStyles =
      "inline-flex items-center justify-center font-bold rounded-lg transition-all duration-200 cursor-pointer text-center whitespace-nowrap";

    const variants = {
      primary:
        "bg-cta text-cta-foreground hover:brightness-110 active:scale-[0.98] animate-pulse-glow-cta",
      secondary:
        "bg-transparent border-2 border-cta text-cta hover:bg-cta/10",
    };

    const sizes = {
      default: "px-6 py-3 text-base",
      lg: "px-8 py-4 text-lg md:text-xl w-full md:w-auto",
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Track A/B/C headline variant on every CTA click
      try {
        const variant = getOrAssignVariant("hero_headline", ["A", "B", "C", "D"]);
        const isCheckout = !!href && /^https?:\/\//i.test(href);
        trackVariantEvent(isCheckout ? "checkout_click" : "cta_click", "hero_headline", variant, {
          cta_label: typeof children === "string" ? children : undefined,
          cta_href: href,
        });
        recordAbClick(
          variant,
          typeof children === "string" ? children : undefined,
          isCheckout ? "checkout_click" : "click",
        );
      } catch {
        // ignore tracking errors
      }

      if (href) {
        if (href.startsWith("#")) {
          const el = document.getElementById(href.slice(1));
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          }
        } else {
          // Encaminha TODOS os parâmetros da URL atual (+ fallback no sessionStorage para UTMs/fbclid)
          try {
            const url = new URL(href);
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.forEach((val, key) => {
              if (val) url.searchParams.set(key, val);
            });
            ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"].forEach((key) => {
              if (!url.searchParams.get(key)) {
                const val = sessionStorage.getItem(key);
                if (val) url.searchParams.set(key, val);
              }
            });
            window.open(url.toString(), "_blank", "noopener,noreferrer");
          } catch {
            window.open(href, "_blank", "noopener,noreferrer");
          }
        }
      }
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);

CTAButton.displayName = "CTAButton";
export default CTAButton;
